import type { IItem, IMenuGroup } from '@order-up/models';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MenuItem } from './MenuItem';

interface MenuGroupProps {
  group: IMenuGroup;
  items: IItem[];
  subgroups?: IMenuGroup[];
  variant?: 'primary' | 'secondary' | 'accent' | 'chart';
  /**
   * ROOT→LEAF chain of group ids ending at `group`. Extended by one id for each
   * subgroup rendered below, and handed to `MenuItem` so prices resolve on the
   * path the item is actually displayed under.
   */
  groupPath: number[];
  /** Every group for the restaurant; the price resolver walks these by id. */
  allMenuGroups: IMenuGroup[];
  /** Seconds per item for auto-scroll timing (default: 3) */
  scrollSecondsPerItem?: number;
}

export const MenuGroup: React.FC<MenuGroupProps> = ({
  group,
  items,
  subgroups = [],
  variant = 'primary',
  groupPath,
  allMenuGroups,
  scrollSecondsPerItem = 3
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrollDuration, setScrollDuration] = useState(30);

  // Check for overflow and calculate scroll duration
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight } = contentRef.current;
        const isOverflowing = scrollHeight > clientHeight + 10; // 10px threshold
        setHasOverflow(isOverflowing);

        if (isOverflowing) {
          // Calculate duration based on content amount
          // More items = longer duration for readability
          const totalItems = items.length + subgroups.reduce((acc, sg) => {
            return acc + (items.filter(item =>
              sg.items?.some(ref => ref.itemId === item.itemId)
            ).length);
          }, 0);

          // Base duration: scrollSecondsPerItem per item, minimum 20s, maximum 60s
          const calculatedDuration = Math.min(60, Math.max(20, totalItems * scrollSecondsPerItem));
          setScrollDuration(calculatedDuration);
        }
      }
    };

    // Check on mount and resize
    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [items, subgroups, scrollSecondsPerItem]);

  // Stable ROOT→LEAF chain per subgroup. `renderContent` runs TWICE per render
  // (the marquee duplicates its content for a seamless loop), so building these
  // inline would hand every MenuItem a new array identity on each call and
  // defeat the price-resolution memo inside it.
  //
  // Declared ABOVE the empty-group early return — hooks must run in the same
  // order on every render, and a group that empties out mid-session would
  // otherwise change the hook count.
  const subgroupPaths = useMemo(() => {
    const paths = new Map<number, number[]>();
    for (const subgroup of subgroups) {
      if (subgroup.menuGroupId != null) {
        paths.set(subgroup.menuGroupId, [...groupPath, subgroup.menuGroupId]);
      }
    }
    return paths;
  }, [subgroups, groupPath]);

  if (items.length === 0 && (!subgroups || subgroups.length === 0)) return null;

  // A special check for the "Daily Specials" layout
  const isDailySpecials = group.name.toLowerCase().includes('daily specials');

  // Get items that belong directly to this group
  const directItems = items.filter(item =>
    group.items?.some(itemRef => itemRef.itemId === item.itemId)
  );

  // Render the menu items content
  const renderContent = () => (
    <>
      {/* Show items that belong directly to this group (non-specials) */}
      {!isDailySpecials && directItems.map((item) => (
        <MenuItem
          key={item.itemId || item.guid}
          item={item}
          menuGroupPath={groupPath}
          menuGroupId={group.menuGroupId}
          allMenuGroups={allMenuGroups}
        />
      ))}

      {/* Show subgroups as sections */}
      {subgroups && subgroups.length > 0 && (
        <div className={`menu-group__subgroups ${isDailySpecials ? 'menu-group__subgroups--specials' : ''}`}>
          {subgroups.map(subgroup => {
            const subgroupItems = items.filter(item =>
              subgroup.items?.some(itemRef => itemRef.itemId === item.itemId)
            );
            if (subgroupItems.length === 0) return null;

            // Render "Daily Specials" differently
            if (isDailySpecials) {
              return (
                <div key={subgroup.menuGroupId || subgroup.guid} className="menu-subgroup menu-subgroup--specials">
                  <div className="menu-subgroup__title menu-subgroup__title--specials">
                    {subgroup.name}
                  </div>
                  <div className="menu-subgroup__items">
                    {subgroupItems.map((item) => (
                      <MenuItem
                        key={item.itemId || item.guid}
                        item={item}
                        menuGroupPath={
                          (subgroup.menuGroupId != null && subgroupPaths.get(subgroup.menuGroupId))
                          || groupPath
                        }
                        menuGroupId={subgroup.menuGroupId}
                        allMenuGroups={allMenuGroups}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            // Default rendering for other subgroups
            return (
              <div key={subgroup.menuGroupId || subgroup.guid} className="menu-subgroup">
                <div className="menu-subgroup__title">
                  {subgroup.name}
                </div>
                <div className="menu-subgroup__items">
                  {subgroupItems.map((item) => (
                    <MenuItem
                      key={item.itemId || item.guid}
                      item={item}
                      menuGroupPath={
                        (subgroup.menuGroupId != null && subgroupPaths.get(subgroup.menuGroupId))
                        || groupPath
                      }
                      menuGroupId={subgroup.menuGroupId}
                      allMenuGroups={allMenuGroups}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // Build content class names
  const contentClasses = [
    'menu-group__content',
    isDailySpecials && 'menu-group__content--specials',
    hasOverflow && 'menu-group__content--auto-scroll menu-group__content--has-overflow'
  ].filter(Boolean).join(' ');

  return (
    <section className={`menu-group menu-group--${variant}`}>
      <div className="menu-group__header">
        <div className="menu-group__title">{group.name}</div>
        <div className="menu-group__divider"></div>
        {group.description && !isDailySpecials && (
          <div className="menu-group__description">{group.description}</div>
        )}
      </div>
      <div
        ref={contentRef}
        className={contentClasses}
        style={hasOverflow ? { '--scroll-duration': `${scrollDuration}s` } as React.CSSProperties : undefined}
      >
        {hasOverflow ? (
          // Wrap content in scrolling container, duplicate for seamless loop
          <div className="menu-group__scroll-wrapper">
            {renderContent()}
            {/* Duplicate content for seamless loop */}
            <div aria-hidden="true" style={{ marginTop: 'var(--space-lg)' }}>
              {renderContent()}
            </div>
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </section>
  );
};
